import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  useTheme,
  Fab,
  Snackbar,
  Alert,
  Button,
  SwipeableDrawer,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Download as DownloadIcon,
  Home as HomeIcon,
  CheckCircle as CheckCircleIcon,
  Handshake as HandshakeIcon,
  Receipt as ReceiptIcon,
  AccountBalance as AccountBalanceIcon,
  Payment as PaymentIcon,
  TrendingUp as TrendingUpIcon,
  MoneyOff as MoneyOffIcon,
  LocalShipping as LocalShippingIcon,
  PriceCheck as PriceCheckIcon,
  Badge as BadgeIcon,
  Business as BusinessIcon,
  Group as GroupIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  Settings as SettingsIcon,
  Assessment as AssessmentIcon,
  Contacts as ContactsIcon,
  Description as DescriptionIcon,
  Calculate as CalculateIcon,
  WhatsApp as WhatsAppIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { Capacitor } from '@capacitor/core';
import { TabType } from '../types';
import { useAuth } from '../context/AuthContext';
import { notificationService } from '../services/notificationService';
import { isService } from '../services/job.service';
import { isAdmin } from '../config/adminConfig';

interface LayoutProps {
  children: React.ReactNode;
  currentTab: TabType;
  onTabChange: (tab: TabType) => void;
  onAddJob?: () => void;
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

// Sabit alt nav yüksekliği (safe-area HARİÇ)
const NAV_HEIGHT = 56;

// Alt barda gösterilen 5 sabit sekme
const PINNED_TABS: TabType[] = ['whatsapp', 'bugun', 'tamamlanmis', 'fatura', 'alacaklar'];

const tabConfig: { id: TabType; label: string; shortLabel: string; icon: React.ReactNode }[] = [
  { id: 'whatsapp', label: 'WA Mesaj', shortLabel: 'WA', icon: <WhatsAppIcon /> },
  { id: 'bugun', label: 'Bugün', shortLabel: 'Bugün', icon: <HomeIcon /> },
  { id: 'tamamlanmis', label: 'Tamamlanmış', shortLabel: 'Bitti', icon: <CheckCircleIcon /> },
  { id: 'fatura', label: 'Fatura', shortLabel: 'Fatura', icon: <ReceiptIcon /> },
  { id: 'alacaklar', label: 'Alacaklar', shortLabel: 'Alacak', icon: <AccountBalanceIcon /> },
  { id: 'komisyon', label: 'Komisyon', shortLabel: 'Komisyon', icon: <HandshakeIcon /> },
  { id: 'odemeler', label: 'Ödemeler', shortLabel: 'Ödeme', icon: <PaymentIcon /> },
  { id: 'kazanc', label: 'Kazanç', shortLabel: 'Kazanç', icon: <TrendingUpIcon /> },
  { id: 'giderler', label: 'Giderler', shortLabel: 'Gider', icon: <MoneyOffIcon /> },
  { id: 'soforler', label: 'Şoförler', shortLabel: 'Şoför', icon: <LocalShippingIcon /> },
  { id: 'fiyatListesi', label: 'Fiyat Listesi', shortLabel: 'Fiyat', icon: <PriceCheckIcon /> },
  { id: 'yetkililer', label: 'Yetkililer', shortLabel: 'Yetkili', icon: <BadgeIcon /> },
  { id: 'firmalar', label: 'Firmalar', shortLabel: 'Firma', icon: <BusinessIcon /> },
  { id: 'team', label: 'Ekip', shortLabel: 'Ekip', icon: <GroupIcon /> },
  { id: 'admin', label: 'Admin', shortLabel: 'Admin', icon: <AdminPanelSettingsIcon /> },
  { id: 'kdvHesaplama', label: 'KDV Hesaplama', shortLabel: 'KDV', icon: <CalculateIcon /> },
  { id: 'fisTasarim', label: 'Fiş Tasarım', shortLabel: 'Fiş', icon: <DescriptionIcon /> },
  { id: 'ayarlar', label: 'Ayarlar', shortLabel: 'Ayarlar', icon: <SettingsIcon /> },
  { id: 'raporlama', label: 'Raporlar', shortLabel: 'Rapor', icon: <AssessmentIcon /> },
  { id: 'crm', label: 'CRM', shortLabel: 'CRM', icon: <ContactsIcon /> },
];

export const Layout: React.FC<LayoutProps> = ({
  children,
  currentTab,
  onTabChange,
  onAddJob,
}) => {
  const theme = useTheme();
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const { user, team, canWrite } = useAuth();
  const [notificationDenied, setNotificationDenied] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const isDark = theme.palette.mode === 'dark';

  const userIsAdmin = isAdmin(user);
  const visibleTabs = tabConfig.filter(tab => {
    if (tab.id === 'admin') return !!userIsAdmin;
    return true;
  });

  // Sabit sekmeler (alt bar)
  const pinnedTabs = visibleTabs.filter(t => PINNED_TABS.includes(t.id));
  // "Diğer" için geri kalanlar
  const moreTabs = visibleTabs.filter(t => !PINNED_TABS.includes(t.id));

  // StatusBar yapılandırması
  useEffect(() => {
    const setupStatusBar = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          const { StatusBar, Style } = await import('@capacitor/status-bar');
          await StatusBar.setOverlaysWebView({ overlay: false });
          await StatusBar.setStyle({ style: Style.Light });
          await StatusBar.setBackgroundColor({ color: '#2563EB' });
        } catch {}
      }
    };
    setupStatusBar();
  }, []);

  // PWA install
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowInstallPrompt(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Bildirimler
  useEffect(() => {
    if (!team?.id) return;
    if (Capacitor.getPlatform() !== 'ios') {
      notificationService.requestPermission()
        .then(p => { if (p === 'denied') setNotificationDenied(true); })
        .catch(() => setNotificationDenied(true));
    }
    isService.getCompletedJobs(team.id)
      .then(jobs => notificationService.checkOverdueItems(jobs))
      .catch(() => {});
  }, [team?.id]);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') { setShowInstallPrompt(false); setDeferredPrompt(null); }
  };

  const handleTabChange = (tabId: TabType) => {
    onTabChange(tabId);
    setMoreOpen(false);
  };

  const isWa = currentTab === 'whatsapp';

  // WA aktifken nav daha kompakt
  const navHeight = isWa ? 40 : NAV_HEIGHT;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        '@supports (height: 100dvh)': { height: '100dvh' },
        overflow: 'hidden',
        bgcolor: 'background.default',
      }}
    >
      {/* ── Top Bar ── */}
      <AppBar position="static" elevation={0} sx={{ flexShrink: 0 }}>
        <Toolbar sx={{ minHeight: { xs: 52, sm: 56 }, px: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/logo-32.png`}
              alt="Logo"
              style={{ width: 28, height: 28, marginRight: 10, borderRadius: 6, objectFit: 'contain' }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
            <Typography
              variant="subtitle1"
              component="div"
              sx={{ fontWeight: 800, fontSize: { xs: '1rem', sm: '1.1rem' }, letterSpacing: '-0.01em' }}
            >
              SevkiyatDefterim
            </Typography>
          </Box>
          {showInstallPrompt && (
            <Button color="inherit" startIcon={<DownloadIcon />} onClick={handleInstallClick} size="small" sx={{ borderRadius: 2, textTransform: 'none' }}>
              Kur
            </Button>
          )}
        </Toolbar>
      </AppBar>

      {/* ── İçerik ── */}
      <Box
        component="main"
        sx={{
          flex: 1,
          minHeight: 0,
          display: isWa ? 'flex' : 'block',
          flexDirection: 'column',
          overflow: isWa ? 'hidden' : 'auto',
          WebkitOverflowScrolling: 'touch',
          bgcolor: 'background.default',
          pt: isWa ? 0 : { xs: 1.5, sm: 2 },
          px: isWa ? 0 : { xs: 1.5, sm: 2.5 },
          // İçerik nav'ın arkasına girmesin
          pb: `calc(${navHeight}px + env(safe-area-inset-bottom, 0px))`,
        }}
      >
        {children}
      </Box>

      {/* ── Alt Navigasyon ── */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          bgcolor: 'background.paper',
          borderTop: `1px solid ${theme.palette.divider}`,
          boxShadow: '0 -1px 4px rgba(0,0,0,0.08)',
          // Safe area: bar görsel alanını genişletir, içerik değil
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          display: 'flex',
          flexWrap: 'nowrap',
          // Sabit 6 item — scroll YOK
          justifyContent: 'space-around',
          alignItems: 'stretch',
        }}
      >
        {/* 5 sabit sekme */}
        {pinnedTabs.map((tab) => {
          const isActive = currentTab === tab.id;
          return (
            <Box
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: isWa ? 'row' : 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: `${navHeight}px`,
                gap: isWa ? 0 : '2px',
                cursor: 'pointer',
                position: 'relative',
                touchAction: 'manipulation',
                userSelect: 'none',
                WebkitTapHighlightColor: 'rgba(0,0,0,0.08)',
                transition: 'background-color 0.1s',
                '&:active': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' },
              }}
            >
              {isActive && !isWa && (
                <Box sx={{
                  position: 'absolute', top: 0, left: '50%',
                  transform: 'translateX(-50%)', width: 20, height: 2,
                  borderRadius: '0 0 4px 4px', bgcolor: 'primary.main',
                }} />
              )}
              <Box sx={{
                fontSize: isWa ? 18 : 22,
                opacity: isActive ? 1 : 0.5,
                color: isActive ? 'primary.main' : 'text.secondary',
                display: 'flex', alignItems: 'center',
                transform: isActive && !isWa ? 'scale(1.1)' : 'scale(1)',
                transition: 'all 0.15s',
              }}>
                {React.cloneElement(tab.icon as React.ReactElement, {
                  fontSize: isWa ? 'small' : 'medium',
                })}
              </Box>
              {!isWa && (
                <Typography sx={{
                  fontSize: '0.58rem',
                  fontWeight: isActive ? 700 : 400,
                  color: isActive ? 'primary.main' : 'text.secondary',
                  lineHeight: 1, whiteSpace: 'nowrap',
                }}>
                  {tab.shortLabel}
                </Typography>
              )}
            </Box>
          );
        })}

        {/* ≡ Diğer butonu */}
        <Box
          onClick={() => setMoreOpen(true)}
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: isWa ? 'row' : 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: `${navHeight}px`,
            gap: isWa ? 0 : '2px',
            cursor: 'pointer',
            touchAction: 'manipulation',
            userSelect: 'none',
            WebkitTapHighlightColor: 'rgba(0,0,0,0.08)',
            '&:active': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' },
          }}
        >
          <Box sx={{
            fontSize: isWa ? 18 : 22,
            opacity: moreTabs.some(t => t.id === currentTab) ? 1 : 0.5,
            color: moreTabs.some(t => t.id === currentTab) ? 'primary.main' : 'text.secondary',
            display: 'flex', alignItems: 'center',
          }}>
            <MenuIcon fontSize={isWa ? 'small' : 'medium'} />
          </Box>
          {!isWa && (
            <Typography sx={{
              fontSize: '0.58rem',
              fontWeight: moreTabs.some(t => t.id === currentTab) ? 700 : 400,
              color: moreTabs.some(t => t.id === currentTab) ? 'primary.main' : 'text.secondary',
              lineHeight: 1, whiteSpace: 'nowrap',
            }}>
              Diğer
            </Typography>
          )}
        </Box>
      </Box>

      {/* ── Bottom Sheet: Diğer Sekmeler ── */}
      <SwipeableDrawer
        anchor="bottom"
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        onOpen={() => setMoreOpen(true)}
        disableSwipeToOpen
        PaperProps={{
          sx: {
            borderRadius: '16px 16px 0 0',
            maxHeight: '60vh',
            pb: 'env(safe-area-inset-bottom, 0px)',
          }
        }}
      >
        {/* Tutma çubuğu */}
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1.5, pb: 0.5 }}>
          <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: 'divider' }} />
        </Box>
        <Typography variant="subtitle2" sx={{ px: 2, pb: 1, fontWeight: 700, color: 'text.secondary' }}>
          Diğer Sekmeler
        </Typography>
        <Box sx={{ overflowY: 'auto', px: 1.5, pb: 2 }}>
          <Grid container spacing={0.5}>
            {moreTabs.map((tab) => {
              const isActive = currentTab === tab.id;
              return (
                <Grid item xs={3} key={tab.id}>
                  <Box
                    onClick={() => handleTabChange(tab.id)}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 1.5,
                      px: 0.5,
                      borderRadius: 2,
                      cursor: 'pointer',
                      touchAction: 'manipulation',
                      userSelect: 'none',
                      minHeight: 72,
                      bgcolor: isActive
                        ? (isDark ? 'rgba(59,130,246,0.15)' : 'rgba(37,99,235,0.08)')
                        : 'transparent',
                      '&:active': { bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' },
                    }}
                  >
                    <Box sx={{
                      color: isActive ? 'primary.main' : 'text.secondary',
                      display: 'flex', mb: 0.5,
                    }}>
                      {React.cloneElement(tab.icon as React.ReactElement, { fontSize: 'medium' })}
                    </Box>
                    <Typography sx={{
                      fontSize: '0.62rem',
                      fontWeight: isActive ? 700 : 400,
                      color: isActive ? 'primary.main' : 'text.secondary',
                      textAlign: 'center', lineHeight: 1.2,
                    }}>
                      {tab.label}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </SwipeableDrawer>

      {/* ── FAB ── */}
      {currentTab === 'bugun' && canWrite && onAddJob && (
        <Fab
          color="primary"
          aria-label="İş Ekle"
          onClick={onAddJob}
          sx={{
            position: 'fixed',
            bottom: `calc(${NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px) + 16px)`,
            right: 20,
            zIndex: 1001,
            width: 52,
            height: 52,
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* ── Snackbarlar ── */}
      <Snackbar
        open={showInstallPrompt}
        autoHideDuration={6000}
        onClose={() => setShowInstallPrompt(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: `calc(${NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px) + 8px) !important` }}
      >
        <Alert onClose={() => setShowInstallPrompt(false)} severity="info" sx={{ borderRadius: 3 }}
          action={<Button color="inherit" size="small" onClick={handleInstallClick}>Kur</Button>}
        >
          SevkiyatDefterim'i telefonunuza kurun!
        </Alert>
      </Snackbar>

      <Snackbar open={notificationDenied} onClose={() => setNotificationDenied(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setNotificationDenied(false)} severity="warning" sx={{ width: '100%', borderRadius: 3 }}>
          Bildirimler engellendi. Tarayıcı ayarlarından izin verin.
        </Alert>
      </Snackbar>
    </Box>
  );
};
